import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { SystemRoundedCheckbox } from "../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { DateInput } from "../../../../components/UIToolkit/Inputs/DateInput";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { RecurringStartPreviewPanel } from "../RecurringStartPreviewPanel";

// The API takes min_start_date as an ISO date string; DateInput speaks Luxon, so this is the single
// place the two representations meet.
const MIN_START_DATE_FORMAT = "yyyy-MM-dd";

type Props = {
	departmentId: string | undefined;
	/** The nurses loaders take no duplicate-match flag, so the control is pointless without these. */
	showAllowDuplicates: boolean;
	allowDuplicates: boolean;
	onAllowDuplicatesChange: (value: boolean) => void;
	showMinStartDate: boolean;
	minStartDate: string;
	onMinStartDateChange: (value: string) => void;
	locked: boolean;
};

/**
 * The two department-level controls, rendered once for the whole run.
 *
 * Both are properties of the department, not of a file. Min start date is a floor on when generated
 * shifts may begin, and template files partition the department by PEOPLE rather than by date range,
 * so one floor applies to all of them. Allow-duplicates is a decision about the client's spelling
 * variants; letting two shifts files in one run disagree would produce inconsistent name matching
 * within a single department state.
 */
export function DataLoadSharedControls({
	departmentId,
	showAllowDuplicates,
	allowDuplicates,
	onAllowDuplicatesChange,
	showMinStartDate,
	minStartDate,
	onMinStartDateChange,
	locked,
}: Props) {
	if (!showAllowDuplicates && !showMinStartDate) return null;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }} data-testid="data-load-shared-controls">
			{showAllowDuplicates && (
				<SystemRoundedCheckbox
					checked={allowDuplicates}
					onChange={onAllowDuplicatesChange}
					disabled={locked}
					label="Allow multiple employees to match same nurse (for CSVs with spelling variants)"
					data-testid="data-load-allow-duplicates"
				/>
			)}

			{showMinStartDate && (
				<Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<DateInput
							label="Min start date (optional)"
							dataTestId="data-load-min-start-date"
							value={minStartDate ? DateTime.fromFormat(minStartDate, MIN_START_DATE_FORMAT) : undefined}
							onChange={(picked) => onMinStartDateChange(picked.toFormat(MIN_START_DATE_FORMAT))}
							formControlSx={{ width: 220 }}
							disabled={locked}
						/>
						{minStartDate && (
							<SystemButton
								buttonVariant="borderless"
								size="small"
								onClick={() => onMinStartDateChange("")}
								disabled={locked}
								data-testid="data-load-min-start-date-clear"
							>
								Clear
							</SystemButton>
						)}
					</Box>
					<Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
						Applies to every template file in this run.
					</Typography>
					<RecurringStartPreviewPanel departmentId={departmentId} minStartDate={minStartDate} />
				</Box>
			)}
		</Box>
	);
}
